import { useCallback, useEffect, useState } from 'react';

import { useLocalStorage } from 'react-use';

import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { analytics } from '../../../services/analytics';
import { postRequest } from '../../../services/api';
import type { AnimationGuideProgress } from '../../../services/types';
import { useStores } from '../../../stores/useStores';
import { ONBOARDING_CONFIG } from '../constants';

import { ONBOARDING_STEP_STORAGE_KEY } from './helper';
import type { RiveEventProperties, BaseRiveEventProperties } from './types';

export function useInteractiveAnimationFTUE() {
  const { onboardingStore, userStore, walletStore, actionEventTriggerStore } = useStores();

  const isOnboardingActive = onboardingStore.currentFtueType === 'interactiveAnimation';
  const shouldClearSavedStep = isOnboardingActive && onboardingStore.isFlowFtue;

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [savedStep, setSavedStep, clearSavedStep] = useLocalStorage<AnimationGuideProgress | null>(
    ONBOARDING_STEP_STORAGE_KEY(userStore.profile?.firebaseUid || ''),
    null,
  );

  useHiddenBodyOverflow(showOnboarding);

  const sendPropertiesAnalytics = useCallback(
    (
      properties: BaseRiveEventProperties,
      fileUrl: string,
      skipStepNumber: number,
      ftueType?: string,
      resumed?: boolean,
    ) => {
      void analytics.sendEvent(properties.event_name, {
        event_parent: 'ftue',
        event_category: 'tutorial',
        event_action: properties.event_action || 'impression',
        step_name: properties.step_name,
        is_skip: properties.isSkip,
        step_number: properties.stageNum,
        url: fileUrl,
        ftue_type: ftueType,
        skip_step_number: skipStepNumber,
        ...(resumed ? { resumed: true } : {}),
        ...(properties.step_name === 'step_1_tutorial'
          ? { triggered_by: onboardingStore.isFlowFtue ? 'flow' : 'sign_up' }
          : {}),
      });
    },
    [onboardingStore],
  );

  const closeFTUE = useCallback(() => {
    onboardingStore.finishFtue();
    setShowOnboarding(false);
    clearSavedStep();
  }, [onboardingStore, clearSavedStep]);

  const onLastStepVisible = useCallback(async () => {
    // The welcome bonus belongs to onboarding; a Flow replay must not pay it again.
    if (onboardingStore.isFlowFtue) {
      return;
    }
    await postRequest('user/ftue-tutorial/finish');
    await walletStore.fetchWallet();
  }, [onboardingStore, walletStore]);

  const finishOnBoardingTourGuide = useCallback(() => {
    if (onboardingStore.isFlowFtue) {
      actionEventTriggerStore.completeFtue();
    } else {
      userStore.updateProfile({ isOnboardingCompleted: true });
    }
    closeFTUE();
  }, [actionEventTriggerStore, closeFTUE, onboardingStore, userStore]);

  const skipOnboardingTourGuide = useCallback(async () => {
    if (!onboardingStore.isFlowFtue) {
      await postRequest('user/ftue-tutorial/finish');
      await walletStore.fetchWallet();
    }
    void analytics.sendEvent('tutorial_skipped', {
      event_parent: 'ftue',
      event_category: 'tutorial',
      triggered_by: 'click',
      event_action: 'click',
      step_name: savedStep?.stepName || '',
      stepNum: savedStep?.stageNum || 0,
    });
    finishOnBoardingTourGuide();
  }, [finishOnBoardingTourGuide, onboardingStore, savedStep, walletStore]);

  const terminateOnboardingTourGuideFlow = useCallback(
    (reason: string, animationType: 'rive') => {
      void analytics.sendEvent('onboarding_terminated', {
        event_parent: 'ftue',
        reason,
        event_category: 'tutorial',
        type: 'interactive_animation',
        animationType,
      });
      finishOnBoardingTourGuide();
    },
    [finishOnBoardingTourGuide],
  );

  const saveStepToLocalStorage = useCallback(
    (properties: BaseRiveEventProperties, fileUrl: string) => {
      if (!('stageNum' in properties)) {
        return;
      }
      const currentStageNum = properties.stageNum || 0;
      if (savedStep && savedStep.stageNum >= currentStageNum) {
        return;
      }
      setSavedStep({
        stepName: properties.step_name,
        riveFileName: fileUrl,
        firebaseUserId: userStore.profile?.firebaseUid || '',
        stageNum: currentStageNum,
        eventName: properties.event_name,
      });
    },
    [savedStep, userStore.profile?.firebaseUid, setSavedStep],
  );

  // The resumed step's own impression is dropped below as already viewed (stageNum <= saved), so a
  // player who resumes and quits on that step would look like one the tutorial never came back for.
  const onGuideRestored = useCallback(
    (fileUrl: string, skipStepNumber?: number, ftueType?: string) => {
      if (!savedStep) {
        return;
      }
      sendPropertiesAnalytics(
        {
          event_name: savedStep.eventName as BaseRiveEventProperties['event_name'],
          step_name: savedStep.stepName,
          stageNum: savedStep.stageNum,
          event_action: 'impression',
        },
        fileUrl,
        skipStepNumber || 0,
        ftueType,
        true,
      );
    },
    [savedStep, sendPropertiesAnalytics],
  );

  const checkIfStageNumberExpired = useCallback(
    (stepNum: number): boolean => {
      if (!savedStep) {
        return false;
      }
      return stepNum <= savedStep.stageNum;
    },
    [savedStep],
  );

  const stepNumberHandler = useCallback(
    (stepNum: number) => {
      if (checkIfStageNumberExpired(stepNum)) {
        return { isStepAlreadyViewed: true };
      }
      return { isStepAlreadyViewed: false };
    },
    [checkIfStageNumberExpired],
  );

  const onGuideEvent = useCallback(
    (
      properties: RiveEventProperties,
      fileUrl: string,
      skipTriggerInput?: { fire: () => void },
      wasInitialSkipTriggered?: boolean,
      skipStepNumber?: number,
      ftueType?: string,
    ) => {
      try {
        if ('stageNum' in properties && properties.stageNum) {
          const { isStepAlreadyViewed } = stepNumberHandler(properties.stageNum);
          if (isStepAlreadyViewed) {
            return;
          }
        }
        if ('isFinish' in properties && properties.isFinish) {
          finishOnBoardingTourGuide();
          return;
        }
        if ('isSkip' in properties && properties.isSkip) {
          void skipOnboardingTourGuide();
          return;
        }
        if ('isLastStep' in properties && properties.isLastStep) {
          if (wasInitialSkipTriggered) {
            skipTriggerInput?.fire();
          }
          void onLastStepVisible();
        }
        if ('event_name' in properties && 'step_name' in properties) {
          saveStepToLocalStorage(properties, fileUrl);
          sendPropertiesAnalytics(properties, fileUrl, skipStepNumber || 0, ftueType);
        }
      } catch (error) {
        terminateOnboardingTourGuideFlow(`error_handling_event: ${(error as Error)?.message}`, 'rive');
      }
    },
    [
      finishOnBoardingTourGuide,
      sendPropertiesAnalytics,
      onLastStepVisible,
      terminateOnboardingTourGuideFlow,
      skipOnboardingTourGuide,
      saveStepToLocalStorage,
      stepNumberHandler,
    ],
  );

  // A replay starts at step 1; a leftover step from an abandoned onboarding run would skip ahead.
  useEffect(() => {
    if (shouldClearSavedStep) {
      clearSavedStep();
    }
  }, [shouldClearSavedStep, clearSavedStep]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (isOnboardingActive) {
      timeoutId = setTimeout(() => setShowOnboarding(true), ONBOARDING_CONFIG.ANIMATION_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOnboardingActive]);

  return {
    showOnboarding: showOnboarding && isOnboardingActive,
    terminateOnboardingTourGuideFlow,
    onGuideEvent,
    onGuideRestored,
    savedStep,
  };
}
